// IMPORTANT: This is not actually part of the Account Module.
// This page is for the separate MFA setup flow which is accessible from the login process and does not use the HomeComponent layout.

import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AngularTokenService } from '@raysuelzer/angular-token';
import { Observable, of } from 'rxjs';
import { finalize, map, takeUntil, tap } from 'rxjs/operators';

import {
  GetCurrentUserGQL, MfaFactor, MfaMethod, RemoveMfaGQL, UserDetailsFragment,
  UserWithTurfAssignmentsFragment
} from 'generated/graphql';

import { MfaService } from 'auth/services/mfa.service';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { e164PhoneValidator } from 'core/validators';
import { SUPPORTED_COUNTRY_CODES } from 'core/geography';

type MfaSetupStep =
  | "select-method"
  | "setup-totp"
  | "setup-sms"
  | "verify-code"
  | "setup-complete";

interface MfaStepConfig {
  key: MfaSetupStep;
  formGroup: FormGroup;
  submitButtonText: (isSubmitting: boolean) => string;
  canProceed: () => boolean;
  /** Called when the step is submitted. Returns an observable that emits the next step or null if no transition. */
  onSubmit: () => Observable<MfaSetupStep | null>; // Returns next step or null if no transition
}

@Component({
  selector: "app-account-setup-mfa",
  templateUrl: "./account-setup-mfa.page.html",
})
export class AccountSetupMfaPage
  extends WithDisposableSubject()
  implements OnInit, OnDestroy {

  /** Each step has a configuration object which defines behavior */
  private mfaStepConfigs: Record<MfaSetupStep, MfaStepConfig>;

  /** Current User */
  currentUser: UserDetailsFragment & UserWithTurfAssignmentsFragment;

  /** Existing MFA factor (if user already has one configured) */
  // not used yet but will be needed to show existing method and allow removal
  existingMfaFactor: MfaFactor | null = null;

  /**
   * When the user arrives with a limited token (MFA_VERIFICATION_REQUIRED), the
   * Apollo error handler passes the method and masked phone as query params.
   * In this mode we skip the getCurrentUser call (it would fail too) and jump
   * straight to the verify-code step.
   * Normalized to the same values as `mfaType`: `MfaMethod.Totp` | `MfaMethod.Sms`.
   */
  limitedTokenMfaMethod: MfaMethod.Totp | MfaMethod.Sms | null = null;

  /** Expose enum to the template for radio `value` / comparisons. */
  readonly mfaMethod = MfaMethod;
  /** Masked phone number shown when resuming a pending SMS setup. */
  maskedPhone: string | null = null;
  /** Set to true while loading data */
  isLoading = true;
  /** Set to true when AJAX in flight  */
  isSubmitting = false;
  /** Error messages  */
  errorMessages: string[] = [];
  /** Current step in the setup flow */
  currentStepKey: MfaSetupStep = "select-method";
  /** Show text code for TOTP setup */
  showTextCode = false;
  /** QR code SVG returned from startMfaSetup (TOTP only) */
  qrCodeSvg: SafeHtml | null = null;
  /** TOTP secret key returned from startMfaSetup (TOTP only) */
  totpSecret: string | null = null;

  constructor(
    private mfaService: MfaService,
    private getCurrentUserGQL: GetCurrentUserGQL,
    private removeMfaGQL: RemoveMfaGQL,
    private router: Router,
    private route: ActivatedRoute,
    private tokenService: AngularTokenService,
    private cd: ChangeDetectorRef,
    private formBuilder: FormBuilder,
  ) {
    super();

    const methodSelectionForm = formBuilder.group({
      mfaType: ["", Validators.required],
    });

    const totpSetupForm = formBuilder.group({});

    const smsSetupForm = formBuilder.group({
      phoneNumber: ["", [Validators.required, e164PhoneValidator]],
    });

    const verificationForm = formBuilder.group({
      mfaToken: [
        "",
        [
          Validators.required,
          Validators.pattern(/^\d{6}$/),
          Validators.minLength(6),
          Validators.maxLength(6),
        ],
      ],
    });

    this.mfaStepConfigs = {
      "select-method": {
        key: "select-method",
        formGroup: methodSelectionForm,
        submitButtonText: (isSubmitting) => isSubmitting ? "Loading..." : "Continue",
        canProceed: () => methodSelectionForm.valid && !this.isSubmitting,
        onSubmit: () => {
          const mfaType = methodSelectionForm.get("mfaType")?.value;
          if (mfaType === MfaMethod.Totp) {
            // Start TOTP setup, which returns an Observable
            return this.mfaService.startTotpSetup().pipe(
              map(result => {
                if (!result.success) {
                  this.errorMessages = result.errors || ['An error occurred. Please try again.'];
                  return null; // Don't transition to next step
                }
                this.qrCodeSvg = result.data!.qrCodeSvg;
                this.totpSecret = result.data!.secret;
                return "setup-totp" as MfaSetupStep;
              })
            );
          } else {
            // Synchronous step change
            return of("setup-sms" as MfaSetupStep);
          }
        }
      },
      "setup-totp": {
        key: "setup-totp",
        formGroup: totpSetupForm,
        submitButtonText: () => "Continue",
        canProceed: () => true,
        onSubmit: () => of("verify-code" as MfaSetupStep)
      },
      "setup-sms": {
        key: "setup-sms",
        formGroup: smsSetupForm,
        submitButtonText: (isSubmitting) => isSubmitting ? "Sending..." : "Send Verification Code",
        canProceed: () => smsSetupForm.valid && !this.isSubmitting,
        onSubmit: () => {
          const phoneNumber = smsSetupForm.get("phoneNumber")?.value;
          return this.mfaService.startSmsSetup(phoneNumber as string).pipe(
            map(result => {
              if (!result.success) {
                this.errorMessages = result.errors || ['An error occurred. Please try again.'];
                return null; // Don't transition to next step
              }
              return "verify-code" as MfaSetupStep;
            })
          );
        }
      },
      "verify-code": {
        key: "verify-code",
        formGroup: verificationForm,
        submitButtonText: (isSubmitting) => isSubmitting ? "Setting up..." : "Complete Setup",
        canProceed: () => verificationForm.valid && !this.isSubmitting,
        onSubmit: () => {
          const code = verificationForm.get("mfaToken")?.value;
          return this.mfaService.verifySetup(code as string).pipe(
            map(result => {
              if (!result.success) {
                this.errorMessages = result.errors || ['An error occurred. Please try again.'];
                return null; // Don't transition to next step
              }
              return "setup-complete" as MfaSetupStep;
            })
          );
        }
      },
      "setup-complete": {
        key: "setup-complete",
        formGroup: methodSelectionForm,
        submitButtonText: () => "Return to Account",
        canProceed: () => true,
        onSubmit: () => {
          this.router.navigateByUrl(`/admin/account/edit/${this.currentUser?.id}`);
          return of(null); // No state change needed
        }
      },
    };

  }

  get currentStepObject(): MfaStepConfig {
    return this.mfaStepConfigs[this.currentStepKey];
  }

  get currentForm(): FormGroup {
    return this.mfaStepConfigs[this.currentStepKey].formGroup;
  }

  get submitButtonText(): string {
    return this.mfaStepConfigs[this.currentStepKey].submitButtonText(this.isSubmitting);
  }

  get canProceed(): boolean {
    return this.mfaStepConfigs[this.currentStepKey].canProceed();
  }

  get isSmsFlow(): boolean {
    return this.mfaStepConfigs["select-method"].formGroup.get("mfaType")?.value === MfaMethod.Sms;
  }

  get orgRequired(): boolean {
    return this.route.snapshot.queryParamMap.get("orgRequired") === "true";
  }

  /** -- Lifecycle hooks -- */
  ngOnInit() {
    const params = this.route.snapshot.queryParamMap;
    const { mfaMethod: limitedMfaMethod, maskedPhone } =
      this.mfaService.getMfaVerificationQueryParams(params);

    if (limitedMfaMethod) {
      // Arrived here with a limited token (MFA_VERIFICATION_REQUIRED).
      // getCurrentUser would fail too, so skip it and jump to verify-code
      // so the user can complete their pending MFA setup.
      this.limitedTokenMfaMethod = limitedMfaMethod;
      this.maskedPhone = maskedPhone;
      this.currentStepKey = "verify-code";
      this.currentForm.patchValue({
        mfaType: limitedMfaMethod,
      });
      this.isLoading = false;
      return;
    }

    // TODO: Move this into a service layer
    this.getCurrentUserGQL
      .watch()
      .valueChanges.pipe(
        takeUntil(this.disposer),
        map(({ data }) => data.getCurrentUser),
      )
      .subscribe((currentUser) => {
        this.currentUser = currentUser!;
        this.isLoading = false;

        if (currentUser?.mfaFactor?.status === "verified") {
          this.existingMfaFactor = currentUser.mfaFactor as MfaFactor;
        }
      });
  }

  onFormSubmit() {
    const currentForm = this.currentForm;
    currentForm.markAllAsTouched();

    if (!currentForm.valid) {
      return;
    }

    this.errorMessages = [];
    this.isSubmitting = true;

    // Call the current step's onSubmit handler, which returns an observable that emits the next step
    // or null if there was an error (errors are handled as data via errorMessages array)
    this.currentStepObject.onSubmit()
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
        takeUntil(this.disposer)
      )
      .subscribe({
        next: (nextStep) => {
          if (nextStep) {
            this.currentStepKey = nextStep;
          }
          // If nextStep is null, stay on current step (errors already set via errorMessages)
        },
        error: (err) => {
          // This should only catch unexpected errors (network errors, etc.)
          this.errorMessages = [err.message || "An unexpected error occurred. Please try again."];
        }
      });
  }

  onBackToMethodSelection() {
    this.limitedTokenMfaMethod = null;
    this.maskedPhone = null;
    this.errorMessages = [];
    this.qrCodeSvg = null;
    this.totpSecret = null;

    // Reset all form groups via config
    Object.values(this.mfaStepConfigs).forEach(config => config.formGroup.reset());

    this.currentStepKey = "select-method";
  }


  onResendCode() {
    if (this.isSubmitting) return;
    this.errorMessages = [];
    this.isSubmitting = true;

    this.mfaService.resendSmsCode()
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: (result) => {
          if (!result.success) {
            this.errorMessages = result.errors || ['Failed to resend code. Please try again.'];
          }
          // Code resent successfully if result.success is true
        },
        error: (err) => {
          this.errorMessages = [err.message || "Failed to resend code. Please try again."];
        },
      });
  }

  onRemoveMfa() {
    const userId =
      this.currentUser?.id ?? this.tokenService.currentUserData?.id;
    if (userId == null) return;

    this.isSubmitting = true;
    this.errorMessages = [];

    this.removeMfaGQL
      .mutate({ input: { userId: String(userId) } },  { errorPolicy: 'all', context: { ignoreErrors: true } })
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
        takeUntil(this.disposer),
      )
      .subscribe({
        next: (result) => {
          const payload = result.data?.removeMfa;
          if (result.errors?.length) {
            this.errorMessages = result.errors.map(e => e.message);
            return;
          }
          this.existingMfaFactor = null;
          this.currentStepKey = "select-method";
        },
        error: () => {
          this.errorMessages = ["Failed to remove MFA. Please try again."];
        },
      });
  }
}
